import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { AxiosError, type AxiosRequestConfig } from 'axios'
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/auth-store'
import { handleServerError } from '@/lib/handle-server-error'
import { subscribeActiveOrganizationInvalidateQueries } from '@/lib/active-organization-query-refetch'
import { initSentry, Sentry } from '@/lib/sentry'
import { SentryErrorFallback } from '@/components/errors/sentry-error-fallback'
import { DirectionProvider } from './context/direction-provider'
import { FontProvider } from './context/font-provider'
import { ThemeProvider } from './context/theme-provider'
import { IdleTimeoutGuard } from '@/components/idle-timeout-guard'
import { resetSessionAndClearQuery } from '@/lib/session-logout'
// Generated Routes
import { routeTree } from './routeTree.gen'
// Styles
import './styles/index.css'
import { isChatwootDisabledForPanel, registerChatwootServiceWorker } from '@/lib/chatwoot'

initSentry()
if (!isChatwootDisabledForPanel()) {
  void registerChatwootServiceWorker()
}

import { buildSafeRedirect } from '@/lib/auth-redirect'
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // eslint-disable-next-line no-console
        if (import.meta.env.DEV) console.log({ failureCount, error })

        if (failureCount >= 0 && import.meta.env.DEV) return false
        if (failureCount > 3 && import.meta.env.PROD) return false

        return !(
          error instanceof AxiosError &&
          [401, 403].includes(error.response?.status ?? 0)
        )
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      refetchOnReconnect: true,
      // Do not keep polling live feeds (ticker / active-calls / etc.) in background tabs.
      refetchIntervalInBackground: false,
      staleTime: 30 * 1000, // 30s — default freshness window
      gcTime: 5 * 60 * 1000, // 5min — keep unused cache
    },
    mutations: {
      onError: (error) => {
        // Axios errors are already surfaced by api-client interceptor.
        if (error instanceof AxiosError) {
          if (error.response?.status === 304) {
            toast.error('Content not modified!')
          }
          return
        }

        Sentry.captureException(error)
        handleServerError(error)
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          toast.error('Session expired!')
          resetSessionAndClearQuery(queryClient)
          const redirect = buildSafeRedirect(`${router.history.location.href}`)
          router.navigate({
            to: '/sign-in',
            ...(redirect ? { search: { redirect } } : {}),
            replace: true,
          })
        }
        if (error.response?.status === 500) {
          const skipGlobal500Redirect =
            (error.config as AxiosRequestConfig & { _skipGlobal500Redirect?: boolean } | undefined)
              ?._skipGlobal500Redirect === true

          if (skipGlobal500Redirect) return

          // Note: Sentry capture already happens in api-client.ts interceptor for 5xx errors.
          // No capture here to avoid duplicate events in Sentry.

          toast.error('Internal Server Error!')
          // Only navigate to error page in production to avoid disrupting HMR in development
          if (import.meta.env.PROD) {
            router.navigate({ to: '/500' })
          }
        }
        if (error.response?.status === 403) {
          // router.navigate("/forbidden", { replace: true });
        }
      }
    },
  }),
})

subscribeActiveOrganizationInvalidateQueries(queryClient)

// Create a new router instance
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  // Reuse preloaded route chunks/data within the default query stale window.
  defaultPreloadStaleTime: 30_000,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Hydrate user once on boot (deduped; does not block render)
void useAuthStore.getState().auth.fetchUser()

// Render the app
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <Sentry.ErrorBoundary
          fallback={({ error, componentStack, eventId, resetError }) => (
            <SentryErrorFallback
              error={error as Error}
              componentStack={componentStack ?? undefined}
              eventId={eventId}
              resetError={resetError}
            />
          )}
        >
          <ThemeProvider>
            <FontProvider>
              <DirectionProvider>
                <IdleTimeoutGuard />
                <RouterProvider router={router} />
              </DirectionProvider>
            </FontProvider>
          </ThemeProvider>
        </Sentry.ErrorBoundary>
      </QueryClientProvider>
    </StrictMode>
  )
}
